import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import {
  Business as BusinessIcon,
  Share as ShareIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Lightbulb as LightbulbIcon,
  Launch as LaunchIcon,
  ThumbUp as ThumbUpIcon,
  Handshake as HandshakeIcon,
  WorkOutline as WorkIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';

const EmptyBusinessNetworkState = ({ onSearchClick }) => {
  const growthSteps = [
    {
      icon: <GroupIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Ask Your Network to Follow Businesses",
      description: "Encourage your connections to follow and engage with businesses in your industry",
      action: "Share with Network",
      color: 'primary'
    },
    {
      icon: <StarIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      title: "Request Business References", 
      description: "Ask your network to refer businesses they've worked with or recommend",
      action: "Get References",
      color: 'warning'
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      title: "Connect with Service Providers",
      description: "Build relationships with professionals who can help grow your business",
      action: "Find Providers",
      color: 'success'
    }
  ];

  const tips = [
    "Quality connections lead to quality business referrals",
    "Active networks grow 3x faster than passive ones",
    "Business relationships compound over time"
  ];

  return (
    <Box sx={{ py: 4 }}>
      {/* Main Visual Header */}
      <Paper 
        variant='outlined' 
        sx={{ 
          p: 4, 
          mb: 3, 
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        <Box sx={{ mb: 3 }}>
          <BusinessIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Grow Your Business Network
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
            Your extended business network is empty. Help us connect you with quality service providers by activating your network!
          </Typography>
        </Box>
        
        <Chip 
          label="🚀 Start Building Today" 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            color: 'white',
            fontSize: '1.1rem',
            px: 2,
            py: 1
          }} 
        />
      </Paper>

      {/* Growth Steps Cards */}
      <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3, fontWeight: 'bold' }}>
        3 Steps to Build Your Business Network
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {growthSteps.map((step, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card 
              variant="outlined" 
              sx={{ 
                height: '100%',
                border: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                }
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={`Step ${index + 1}`} 
                    size="small" 
                    color={step.color}
                    sx={{ mb: 2 }}
                  />
                  {step.icon}
                </Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {step.description}
                </Typography>
                <Button
                  variant="contained"
                  color={step.color}
                  fullWidth
                  startIcon={<LaunchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  {step.action}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Why This Matters Section */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none', bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <LightbulbIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h6" fontWeight="bold">
            Why Building Your Business Network Matters
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          {tips.map((tip, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ThumbUpIcon color="success" sx={{ fontSize: 20 }} />
                <Typography variant="body2">
                  {tip}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ textAlign: 'center', mb: 3 }}>
          Get Started Right Now
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<PersonAddIcon />}
              onClick={onSearchClick}
              sx={{ 
                py: 2,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
              }}
            >
              Search for People to Connect
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<ShareIcon />}
              sx={{ 
                py: 2,
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': { borderWidth: 2 }
              }}
            >
              Share Your Profile
            </Button>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            💡 <strong>Pro Tip:</strong> The more active your network is with businesses, the more quality businesses you'll discover!
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Start by asking 3 people in your network to follow businesses they know and trust.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmptyBusinessNetworkState;